/**
 * This is not a React component, but will render and maintain an SVG plot.
 * This needs to be passed to the MemroyBank component.
 *
 * To initially render the nodes, showVisual() must be called.
 * To get the SVG DOM element, call the getSVG() method.
 *
 * @requires tipped
 */

import d3 from 'd3';
import * as Config from 'es6/components/memory-bank/config';
import ProgressVisual from 'es6/components/memory-bank/visuals/progress';
import DifficultyVisual from 'es6/components/memory-bank/visuals/difficulty';

export default class MemoryBankPlot {
  /**
   * Sets up the SVG element.
   *
   * @param {array} memoryBankNodes - Array of MemoryBankNode
   */
  constructor(memoryBankNodes = []) {
    this.nodes = memoryBankNodes;

    // This contains the D3 selection after a transition. Can be used
    // to access entered and exited nodes.
    this.selection = null;

    // Set up the SVG DOM element.
    this._svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    this._svg.setAttribute('width', '100%');
    this._svg.setAttribute('height', '100%');
    this._svg.setAttribute('viewBox', `0 0 ${Config.ViewBox.WIDTH} ${Config.ViewBox.HEIGHT}`);

    // Create and append two containers. One for labels and the other for nodes.
    this.layoutsContainer = d3.select(this._svg).append('g').classed('layout', true);
    this.nodesContainer = d3.select(this._svg).append('g').classed('nodes', true);

    // Create the visuals and layouts.
    this._visual = null;
    this._visuals = {
      difficulty: new DifficultyVisual(this),
      progress: new ProgressVisual(this),
    };
  }

  /**
   * Returns the SVG that contains the memory bank nodes.
   *
   * @returns {HTMLElement} SVG DOM element
   */
  getSVG() {
    return this._svg;
  }

  /**
   * Updates selection, which can be used by the visuals to handle entering,
   * updating and exiting nodes. Be sure to specify the ID here, because if
   * new memory bank nodes are created, we still want to associate the same IDs.
   */
  _setupSelection() {
    this.selection = this.nodesContainer.selectAll('.node').data(this.nodes, d => d.id);
  }

  /**
   * Updates the nodes on the plot to position them where the
   *
   * @param {String} [progress|difficulty] visual
   */
  showVisual(visual = 'progress') {
    const nextVisual = this._visuals[visual];

    if (nextVisual && this._visual !== nextVisual) {
      this._setupSelection();

      if (this._visual !== null) {
        this._visual.hide();
      }

      this._visual = nextVisual;
      this._visual.show();
    }
  }

  /**
   * Cleans up all the data binding and removes the elements.
   */
  remove() {
    this.nodesContainer.data([]).exit().remove();
  }

  /**
   * Updates the nodes on the current visual.
   *
   * params {Array} nodes
   */
  update(memoryBankNodes = []) {
    this.nodes = memoryBankNodes;
    this._setupSelection();
    this._visual.update();
  }
}
